import {
  Alert,
  Badge,
  Button,
  Card,
  type Column,
  DataTable,
  Modal,
  PageHeader,
  SelectField,
  TextAreaField,
  TextField,
} from '@safepos/ui';
import {
  FEATURE_GROUPS,
  FEATURE_KEYS,
  FEATURES,
  type FeatureKey,
  formatRupiah,
  resolveFeatures,
} from '@safepos/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { platformApi } from '../lib/api';
import { handOffImpersonation } from '../lib/auth';
import { Bars } from './ReportPages';
import { usePlatformAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';

export function PlatformLoginPage() {
  const { status, login } = usePlatformAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const s = useSubmit(() => login(email, password, totp || undefined));
  if (status === 'authenticated') return <Navigate to="/admin/kesehatan" replace />;
  return (
    <main className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <img
          src="/logo.png"
          alt="SafePOS"
          className="mx-auto h-20 w-auto max-w-full rounded-xl p-2"
          style={{ background: '#fff' }}
        />
        <p className="text-center text-sm font-medium text-slate-600">Super Admin</p>
        <Card title="Masuk platform">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void s.run();
            }}
          >
            {s.error && <Alert>{s.error}</Alert>}
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <TextField
              label="Kode autentikator (bila aktif)"
              inputMode="numeric"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
            />
            <Button type="submit" className="w-full" disabled={s.busy}>
              Masuk
            </Button>
            <p className="text-center text-sm">
              <Link to="/admin/lupa" className="text-brand-700">
                Lupa kata sandi?
              </Link>
            </p>
          </form>
        </Card>
      </div>
    </main>
  );
}

/** Minta tautan reset lewat email (/admin/lupa) atau pasang kata sandi baru dari tautan itu (/admin/atur-ulang?token=…). */
export function PlatformForgotPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const s = useSubmit(async () => {
    if (token) {
      if (password !== confirm) throw new Error('Konfirmasi kata sandi tidak sama');
      await platformApi.post('/admin/auth/password/reset', { token, password });
      setDone('Kata sandi berhasil diubah. Silakan masuk dengan kata sandi baru.');
    } else {
      await platformApi.post('/admin/auth/password/forgot', { email });
      setDone(
        'Jika email terdaftar sebagai Super Admin, tautan pengaturan ulang sudah dikirim (berlaku 1 jam).',
      );
    }
  });
  return (
    <main className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <img
          src="/logo.png"
          alt="SafePOS"
          className="mx-auto h-20 w-auto max-w-full rounded-xl p-2"
          style={{ background: '#fff' }}
        />
        <p className="text-center text-sm font-medium text-slate-600">Super Admin</p>
        <Card title={token ? 'Atur kata sandi baru' : 'Lupa kata sandi'}>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void s.run();
            }}
          >
            {s.error && <Alert>{s.error}</Alert>}
            {done && <Alert tone="success">{done}</Alert>}
            {token ? (
              <>
                <TextField
                  label="Kata sandi baru"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <TextField
                  label="Ulangi kata sandi baru"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </>
            ) : (
              <TextField
                label="Email Super Admin"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            )}
            <Button type="submit" className="w-full" disabled={s.busy || !!done}>
              {token ? 'Simpan kata sandi' : 'Kirim tautan reset'}
            </Button>
            <p className="text-center text-sm">
              <Link to="/admin/masuk" className="text-brand-700">
                Kembali ke halaman masuk
              </Link>
            </p>
          </form>
        </Card>
      </div>
    </main>
  );
}

/** Ganti kata sandi saat sudah masuk. Setelah berhasil semua sesi keluar. */
export function PlatformAccountPage() {
  const { admin, logout } = usePlatformAuth();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const s = useSubmit(async () => {
    if (password !== confirm) throw new Error('Konfirmasi kata sandi tidak sama');
    await platformApi.post('/admin/auth/password/change', { currentPassword: current, password });
    setDone(true);
    setCurrent('');
    setPassword('');
    setConfirm('');
    setTimeout(() => void logout(), 1500);
  });
  return (
    <>
      <PageHeader title="Akun" subtitle={admin?.email ?? ''} />
      <Card title="Ganti kata sandi">
        <form
          className="max-w-sm space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void s.run();
          }}
        >
          {s.error && <Alert>{s.error}</Alert>}
          {done && (
            <Alert tone="success">
              Kata sandi diganti. Anda akan keluar; masuk lagi dengan kata sandi baru.
            </Alert>
          )}
          <TextField
            label="Kata sandi saat ini"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
          <TextField
            label="Kata sandi baru"
            type="password"
            autoComplete="new-password"
            hint="Minimal 10 karakter"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <TextField
            label="Ulangi kata sandi baru"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
          <Button type="submit" disabled={s.busy || done}>
            Simpan
          </Button>
        </form>
      </Card>
    </>
  );
}

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  businessType: string;
  status: string;
  plan: { code: string; name: string } | null;
  trialEndsAt: string | null;
  createdAt: string;
  outlets: number;
  employees: number;
  sales30d: number;
  orders30d: number;
  lastOrderDate: string | null;
}
const tone = (s: string) =>
  (s === 'active'
    ? 'success'
    : s === 'trial'
      ? 'info'
      : s === 'suspended'
        ? 'danger'
        : 'neutral') as 'success' | 'info' | 'danger' | 'neutral';
const statusText: Record<string, string> = {
  trial: 'Uji coba',
  active: 'Aktif',
  suspended: 'Ditangguhkan',
  cancelled: 'Berhenti',
};
const dt = (s: string | null) =>
  s ? new Date(s).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export function TenantsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['p-tenants', q, status],
    queryFn: () =>
      platformApi.get<{ data: TenantRow[] }>(
        `/admin/tenants?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}${status ? `&status=${status}` : ''}`,
      ),
  });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'suspend' | 'activate' }) =>
      platformApi.post(`/admin/tenants/${id}/${action}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['p-tenants'] }),
  });
  const cols: Column<TenantRow>[] = [
    {
      header: 'Bisnis',
      cell: (t) => (
        <div>
          <Link to={`/admin/tenant/${t.id}`} className="font-medium text-brand-700 hover:underline">
            {t.name}
          </Link>
          <div className="text-xs text-slate-500">
            {t.slug} · {t.businessType}
          </div>
        </div>
      ),
    },
    { header: 'Paket', cell: (t) => t.plan?.name ?? '—' },
    { header: 'Outlet / Pegawai', cell: (t) => `${t.outlets} / ${t.employees}` },
    {
      header: 'Penjualan 30 hari',
      cell: (t) => (
        <div>
          <div>{formatRupiah(t.sales30d)}</div>
          <div className="text-xs text-slate-500">
            {t.orders30d.toLocaleString('id-ID')} transaksi
          </div>
        </div>
      ),
    },
    { header: 'Transaksi terakhir', cell: (t) => t.lastOrderDate ?? '—' },
    {
      header: 'Status',
      cell: (t) => <Badge tone={tone(t.status)}>{statusText[t.status] ?? t.status}</Badge>,
    },
    {
      header: '',
      className: 'text-right',
      cell: (t) => (
        <div className="flex justify-end gap-2">
          <Link
            to={`/admin/tenant/${t.id}`}
            className="inline-flex min-h-[40px] items-center rounded-lg bg-slate-100 px-4 text-sm font-medium hover:bg-slate-200"
          >
            Detail
          </Link>
          {t.status === 'suspended' ? (
            <Button
              variant="secondary"
              onClick={() => act.mutate({ id: t.id, action: 'activate' })}
            >
              Aktifkan
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={() =>
                confirm(
                  `Tangguhkan ${t.name}? Semua pengguna dan perangkatnya langsung diblokir.`,
                ) && act.mutate({ id: t.id, action: 'suspend' })
              }
            >
              Tangguhkan
            </Button>
          )}
        </div>
      ),
    },
  ];
  return (
    <>
      <PageHeader title="Tenant" subtitle="Seluruh bisnis yang terdaftar" />
      {(error || act.error) && <Alert>{((error ?? act.error) as Error).message}</Alert>}
      <Card
        actions={
          <div className="flex items-end gap-3">
            <div className="w-44">
              <SelectField
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">Semua</option>
                {Object.entries(statusText).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </SelectField>
            </div>
            <div className="w-64">
              <TextField label="Cari" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>
        }
      >
        {isLoading ? 'Memuat…' : <DataTable columns={cols} rows={data?.data ?? []} />}
      </Card>
    </>
  );
}

// ======================= DETAIL TENANT =======================
interface Detail {
  tenant: {
    id: string;
    name: string;
    slug: string;
    businessType: string;
    status: string;
    trialEndsAt: string | null;
    createdAt: string;
  };
  plan: { id: string; code: string; name: string; priceMonthly: number } | null;
  owner: { id: string; name: string; email: string } | null;
  usage: { key: string; label: string; used: number; limit: number | null }[];
  outlets: { id: string; name: string; code: string | null; mode: string; timezone: string }[];
  devices: {
    id: string;
    name: string;
    type: string;
    outlet: string;
    lastSeenAt: string | null;
    revoked: boolean;
  }[];
  users: { id: string; name: string; email: string; role: string; status: string }[];
  sales30d: {
    orders: number;
    net: number;
    series: { date: string; net: number; orders: number }[];
  };
  health: {
    flaggedOrders: number;
    negativeStock: number;
    pendingPayments: number;
    paymentsMissingOrder: number;
  };
  features: {
    key: string;
    label: string;
    enabled: boolean;
    override: boolean | null;
    source: 'override' | 'plan' | 'default';
  }[];
  recentAudit: {
    at: string;
    actorType: string;
    action: string;
    entity: string;
    ip: string | null;
  }[];
}

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const key = ['p-tenant', id];
  const q = useQuery({
    queryKey: key,
    queryFn: () => platformApi.get<Detail>(`/admin/tenants/${id}`),
  });
  const plans = useQuery({
    queryKey: ['p-plans'],
    queryFn: () => platformApi.get<PlanRow[]>('/admin/plans'),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const setPlan = useSubmit(async (planId: string) => {
    await platformApi.post(`/admin/tenants/${id}/plan`, { planId });
    refresh();
  });
  const feature = useSubmit(async (k: string, enabled: boolean | null) => {
    await platformApi.patch(`/admin/tenants/${id}/features`, { key: k, enabled });
    refresh();
  });
  const status = useSubmit(async (action: 'suspend' | 'activate') => {
    await platformApi.post(`/admin/tenants/${id}/${action}`);
    refresh();
  });
  const [imp, setImp] = useState<{ reason: string } | null>(null);
  const impersonate = useSubmit(async () => {
    const r = await platformApi.post<{
      accessToken: string;
      expiresIn: number;
      tenant: { name: string };
    }>(`/admin/tenants/${id}/impersonate`, { reason: imp!.reason.trim() });
    handOffImpersonation({
      token: r.accessToken,
      exp: Date.now() + r.expiresIn * 1000,
      tenantName: r.tenant.name,
    });
    window.open('/office/dasbor?imp=1', '_blank');
    setImp(null);
    refresh();
  });
  const d = q.data;
  if (q.isLoading) return <p>Memuat…</p>;
  if (q.error || !d)
    return <Alert>{(q.error as Error | null)?.message ?? 'Tenant tidak ditemukan'}</Alert>;
  const t = d.tenant;
  return (
    <>
      <PageHeader
        title={t.name}
        subtitle={`${t.slug} · ${t.businessType} · terdaftar ${dt(t.createdAt)}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/admin/tenant"
              className="inline-flex min-h-[40px] items-center rounded-lg bg-slate-100 px-4 text-sm font-medium hover:bg-slate-200"
            >
              ← Daftar
            </Link>
            <Button
              variant="secondary"
              onClick={() => setImp({ reason: '' })}
              disabled={t.status === 'suspended' || !d.owner}
            >
              Masuk sebagai tenant (dukungan)
            </Button>
            {t.status === 'suspended' ? (
              <Button onClick={() => void status.run('activate')}>Aktifkan</Button>
            ) : (
              <Button
                variant="danger"
                onClick={() => confirm(`Tangguhkan ${t.name}?`) && void status.run('suspend')}
              >
                Tangguhkan
              </Button>
            )}
          </div>
        }
      />
      {(setPlan.error || feature.error || status.error) && (
        <Alert>{setPlan.error ?? feature.error ?? status.error}</Alert>
      )}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {(
          [
            [
              'Status',
              <Badge key="s" tone={tone(t.status)}>
                {statusText[t.status] ?? t.status}
              </Badge>,
            ],
            ['Penjualan 30 hari', formatRupiah(d.sales30d.net)],
            ['Transaksi 30 hari', d.sales30d.orders.toLocaleString('id-ID')],
            [
              'Pemilik',
              d.owner ? (
                <span key="o" className="text-base">
                  {d.owner.name}
                  <span className="block text-xs font-normal text-slate-500">{d.owner.email}</span>
                </span>
              ) : (
                '—'
              ),
            ],
          ] as const
        ).map(([k, v]) => (
          <Card key={k}>
            <div className="text-sm text-slate-600">{k}</div>
            <div className="text-2xl font-semibold">{v}</div>
          </Card>
        ))}
      </div>
      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card title="Penjualan 30 hari terakhir">
          {d.sales30d.series.length ? (
            <Bars data={d.sales30d.series.map((x) => ({ label: x.date.slice(8), value: x.net }))} />
          ) : (
            <p className="text-sm text-slate-500">Belum ada penjualan.</p>
          )}
        </Card>
        <Card title="Paket & pemakaian">
          <div className="mb-3 flex items-end gap-2">
            <div className="flex-1">
              <SelectField
                label="Paket"
                value={d.plan?.id ?? ''}
                onChange={(e) =>
                  e.target.value &&
                  confirm('Ubah paket tenant ini?') &&
                  void setPlan.run(e.target.value)
                }
              >
                {!d.plan && <option value="">— belum ada —</option>}
                {plans.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {formatRupiah(p.priceMonthly)}/bln
                  </option>
                ))}
              </SelectField>
            </div>
          </div>
          {t.trialEndsAt && (
            <p className="mb-2 text-xs text-slate-500">Uji coba sampai {dt(t.trialEndsAt)}</p>
          )}
          <ul className="space-y-2 text-sm">
            {d.usage.map((u) => (
              <li key={u.key}>
                <div className="flex justify-between">
                  <span>{u.label}</span>
                  <span>
                    {u.used}
                    {u.limit != null ? ` / ${u.limit}` : ''}
                  </span>
                </div>
                {u.limit != null && (
                  <div className="h-1.5 rounded bg-slate-100">
                    <div
                      className={`h-1.5 rounded ${u.used >= u.limit ? 'bg-red-500' : 'bg-brand-500'}`}
                      style={{ width: `${Math.min(100, (u.used / u.limit) * 100)}%` }}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500">
            Tagihan langganan otomatis belum tersedia (menunggu integrasi akun Midtrans platform).
          </p>
        </Card>
      </div>
      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card title="Kesehatan tenant">
          <ul className="space-y-1 text-sm">
            <li className="flex justify-between">
              <span>Transaksi offline perlu ditinjau</span>
              <b className={d.health.flaggedOrders ? 'text-amber-600' : ''}>
                {d.health.flaggedOrders}
              </b>
            </li>
            <li className="flex justify-between">
              <span>Barang stok negatif</span>
              <b className={d.health.negativeStock ? 'text-amber-600' : ''}>
                {d.health.negativeStock}
              </b>
            </li>
            <li className="flex justify-between">
              <span>Pembayaran non-tunai menunggu</span>
              <b>{d.health.pendingPayments}</b>
            </li>
            <li className="flex justify-between">
              <span>Pembayaran sukses tanpa order</span>
              <b className={d.health.paymentsMissingOrder ? 'text-red-600' : ''}>
                {d.health.paymentsMissingOrder}
              </b>
            </li>
          </ul>
        </Card>
        <Card title="Fitur">
          {FEATURE_GROUPS.map((g) => (
            <section key={g.title} className="mb-3 last:mb-0">
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {g.title}
              </h3>
              <ul className="divide-y text-sm">
                {g.keys
                  .map((k) => d.features.find((x) => x.key === k))
                  .filter((f): f is NonNullable<typeof f> => !!f)
                  .map((f) => (
                    <li key={f.key} className="flex items-center justify-between gap-2 py-2">
                      <span>
                        {f.label}
                        <span className="block text-xs text-slate-500">
                          {f.source === 'override'
                            ? 'Dipaksa Super Admin'
                            : f.source === 'plan'
                              ? 'Ikut paket'
                              : 'Bawaan (aktif)'}
                        </span>
                      </span>
                      <select
                        aria-label={`Fitur ${f.label}`}
                        className="rounded-lg border border-slate-300 px-2 py-1"
                        value={f.override === null ? 'auto' : f.override ? 'on' : 'off'}
                        onChange={(e) =>
                          void feature.run(
                            f.key,
                            e.target.value === 'auto' ? null : e.target.value === 'on',
                          )
                        }
                      >
                        <option value="auto">
                          Ikut paket (
                          {f.override === null ? (f.enabled ? 'aktif' : 'nonaktif') : '…'})
                        </option>
                        <option value="on">Paksa aktif</option>
                        <option value="off">Paksa nonaktif</option>
                      </select>
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </Card>
      </div>
      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card title={`Outlet (${d.outlets.length})`}>
          <ul className="divide-y text-sm">
            {d.outlets.map((o) => (
              <li key={o.id} className="flex justify-between py-1.5">
                <span>
                  {o.name}
                  {o.code ? ` · ${o.code}` : ''}
                </span>
                <span className="text-slate-500">
                  {o.mode} · {o.timezone}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title={`Pengguna Back Office (${d.users.length})`}>
          <ul className="divide-y text-sm">
            {d.users.map((u) => (
              <li key={u.id} className="flex justify-between py-1.5">
                <span>
                  {u.name}
                  <span className="block text-xs text-slate-500">{u.email}</span>
                </span>
                <span className="text-slate-500">
                  {u.role}
                  {u.status !== 'active' ? ` · ${u.status}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card title={`Perangkat (${d.devices.filter((x) => !x.revoked).length} aktif)`}>
          <ul className="divide-y text-sm">
            {d.devices.map((x) => (
              <li key={x.id} className="flex justify-between py-1.5">
                <span className={x.revoked ? 'text-slate-400 line-through' : ''}>
                  {x.name}
                  <span className="block text-xs text-slate-500">
                    {x.type.toUpperCase()} · {x.outlet}
                  </span>
                </span>
                <span className="text-xs text-slate-500">
                  {x.revoked
                    ? 'dicabut'
                    : x.lastSeenAt
                      ? `terlihat ${dt(x.lastSeenAt)}`
                      : 'belum pernah'}
                </span>
              </li>
            ))}
            {d.devices.length === 0 && (
              <li className="py-3 text-slate-500">Belum ada perangkat.</li>
            )}
          </ul>
        </Card>
        <Card title="Aktivitas terbaru">
          <ul className="divide-y text-xs">
            {d.recentAudit.map((a, i) => (
              <li key={i} className="flex justify-between gap-2 py-1.5">
                <span>
                  <b>{a.action}</b>{' '}
                  <span className="text-slate-500">
                    · {a.entity} · {a.actorType}
                  </span>
                </span>
                <span className="shrink-0 text-slate-500">{dt(a.at)}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Modal open={!!imp} title="Masuk sebagai tenant (sesi dukungan)" onClose={() => setImp(null)}>
        {imp && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void impersonate.run();
            }}
          >
            {impersonate.error && <Alert>{impersonate.error}</Alert>}
            <Alert tone="info">
              Anda akan membuka Back Office {t.name} sebagai pemilik selama 30 menit di tab baru.
              Alasan dicatat di log audit tenant, seluruh aksi tercatat atas nama Super Admin, dan
              keamanan akun (TOTP) tidak bisa diubah.
            </Alert>
            <TextAreaField
              label="Alasan (wajib, min. 10 karakter)"
              value={imp.reason}
              onChange={(e) => setImp({ reason: e.target.value })}
              required
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setImp(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={impersonate.busy || imp.reason.trim().length < 10}>
                Mulai sesi dukungan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ======================= KESEHATAN PLATFORM =======================
interface Health {
  generatedAt: string;
  tenants: Record<string, number>;
  newTenants7d: number;
  orders24h: { count: number; net: number };
  sync: { flaggedUnreviewed: number; topTenants: { tenant: string; flagged: number }[] };
  payments24h: {
    byStatus: Record<string, number>;
    stuckPending: number;
    successWithoutOrder: number;
  };
  devices: {
    active: number;
    stale: { name: string; tenant: string; type: string; lastSeenAt: string | null }[];
  };
  negativeStockLevels: number;
  server: {
    node: string;
    uptimeSec: number;
    rssMb: number;
    heapMb: number;
    database: string;
    queue: string;
  };
}

export function HealthPage() {
  const q = useQuery({
    queryKey: ['p-health'],
    refetchInterval: 30_000,
    queryFn: () => platformApi.get<Health>('/admin/health'),
  });
  const h = q.data;
  if (q.isLoading) return <p>Memuat…</p>;
  if (q.error || !h) return <Alert>{(q.error as Error | null)?.message ?? 'Gagal memuat'}</Alert>;
  const bad = h.payments24h.successWithoutOrder + h.payments24h.stuckPending;
  const kpi: [string, string | number, boolean?][] = [
    ['Tenant aktif / uji coba', `${h.tenants.active ?? 0} / ${h.tenants.trial ?? 0}`],
    ['Tenant baru (7 hari)', h.newTenants7d],
    ['Transaksi 24 jam', h.orders24h.count.toLocaleString('id-ID')],
    ['Penjualan 24 jam', formatRupiah(h.orders24h.net)],
    ['Perangkat aktif', h.devices.active],
    ['Transaksi perlu ditinjau', h.sync.flaggedUnreviewed, h.sync.flaggedUnreviewed > 0],
    ['Pembayaran bermasalah', bad, bad > 0],
    ['Stok negatif', h.negativeStockLevels, h.negativeStockLevels > 0],
  ];
  return (
    <>
      <PageHeader
        title="Kesehatan Platform"
        subtitle={`Diperbarui ${dt(h.generatedAt)} · otomatis tiap 30 detik`}
      />
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpi.map(([k, v, warn]) => (
          <Card key={k}>
            <div className="text-sm text-slate-600">{k}</div>
            <div className={`text-2xl font-semibold ${warn ? 'text-amber-600' : ''}`}>{v}</div>
          </Card>
        ))}
      </div>
      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card title="Sinkronisasi">
          <p className="mb-2 text-sm text-slate-600">
            Transaksi offline yang diterima dengan penanda tinjauan dan belum ditinjau, per tenant:
          </p>
          <ul className="divide-y text-sm">
            {h.sync.topTenants.map((t) => (
              <li key={t.tenant} className="flex justify-between py-1.5">
                <span>{t.tenant}</span>
                <b>{t.flagged}</b>
              </li>
            ))}
            {h.sync.topTenants.length === 0 && <li className="py-3 text-slate-500">Tidak ada.</li>}
          </ul>
        </Card>
        <Card title="Pembayaran non-tunai (24 jam)">
          <ul className="space-y-1 text-sm">
            {Object.entries(h.payments24h.byStatus).map(([k, v]) => (
              <li key={k} className="flex justify-between">
                <span>{k}</span>
                <b>{v}</b>
              </li>
            ))}
            {Object.keys(h.payments24h.byStatus).length === 0 && (
              <li className="text-slate-500">Belum ada.</li>
            )}
            <li className="flex justify-between border-t pt-1">
              <span>Menunggu &gt; 20 menit</span>
              <b className={h.payments24h.stuckPending ? 'text-amber-600' : ''}>
                {h.payments24h.stuckPending}
              </b>
            </li>
            <li className="flex justify-between">
              <span>Sukses tanpa order</span>
              <b className={h.payments24h.successWithoutOrder ? 'text-red-600' : ''}>
                {h.payments24h.successWithoutOrder}
              </b>
            </li>
          </ul>
        </Card>
      </div>
      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <Card title="Perangkat tidak terlihat > 24 jam">
          <ul className="divide-y text-sm">
            {h.devices.stale.map((d, i) => (
              <li key={i} className="flex justify-between py-1.5">
                <span>
                  {d.name}
                  <span className="block text-xs text-slate-500">
                    {d.tenant} · {d.type.toUpperCase()}
                  </span>
                </span>
                <span className="text-xs text-slate-500">
                  {d.lastSeenAt ? dt(d.lastSeenAt) : 'belum pernah'}
                </span>
              </li>
            ))}
            {h.devices.stale.length === 0 && (
              <li className="py-3 text-slate-500">Semua perangkat aktif terlihat.</li>
            )}
          </ul>
        </Card>
        <Card title="Server">
          <ul className="space-y-1 text-sm">
            <li className="flex justify-between">
              <span>Basis data</span>
              <b>{h.server.database}</b>
            </li>
            <li className="flex justify-between">
              <span>Node.js</span>
              <b>{h.server.node}</b>
            </li>
            <li className="flex justify-between">
              <span>Uptime</span>
              <b>
                {Math.floor(h.server.uptimeSec / 3600)} j{' '}
                {Math.floor((h.server.uptimeSec % 3600) / 60)} m
              </b>
            </li>
            <li className="flex justify-between">
              <span>Memori (RSS / heap)</span>
              <b>
                {h.server.rssMb} / {h.server.heapMb} MB
              </b>
            </li>
            <li className="flex justify-between">
              <span>Antrean job</span>
              <b>{h.server.queue}</b>
            </li>
          </ul>
        </Card>
      </div>
    </>
  );
}

// ======================= PENGUMUMAN =======================
interface Announcement {
  id: string;
  title: string;
  body: string;
  level: 'info' | 'warning' | 'critical';
  startsAt: string | null;
  endsAt: string | null;
  active: boolean;
}
const levelText = { info: 'Info', warning: 'Peringatan', critical: 'Penting' };
const levelTone = { info: 'info', warning: 'warning', critical: 'danger' } as const;
const toLocalInput = (s: string | null) =>
  s
    ? new Date(new Date(s).getTime() - new Date(s).getTimezoneOffset() * 60_000)
        .toISOString()
        .slice(0, 16)
    : '';

export function AnnouncementsPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['p-ann'],
    queryFn: () => platformApi.get<Announcement[]>('/admin/announcements'),
  });
  const [form, setForm] = useState<{
    id?: string;
    title: string;
    body: string;
    level: Announcement['level'];
    startsAt: string;
    endsAt: string;
    active: boolean;
  } | null>(null);
  const save = useSubmit(async () => {
    const body = {
      title: form!.title,
      body: form!.body,
      level: form!.level,
      active: form!.active,
      startsAt: form!.startsAt ? new Date(form!.startsAt).toISOString() : null,
      endsAt: form!.endsAt ? new Date(form!.endsAt).toISOString() : null,
    };
    if (form!.id) await platformApi.patch(`/admin/announcements/${form!.id}`, body);
    else await platformApi.post('/admin/announcements', body);
    void qc.invalidateQueries({ queryKey: ['p-ann'] });
    setForm(null);
  });
  const del = useSubmit(async (a: Announcement) => {
    if (!confirm(`Hapus pengumuman "${a.title}"?`)) return;
    await platformApi.del(`/admin/announcements/${a.id}`);
    void qc.invalidateQueries({ queryKey: ['p-ann'] });
  });
  const cols: Column<Announcement>[] = [
    {
      header: 'Judul',
      cell: (a) => (
        <div>
          <div className="font-medium">{a.title}</div>
          <div className="max-w-md truncate text-xs text-slate-500">{a.body}</div>
        </div>
      ),
    },
    {
      header: 'Tingkat',
      cell: (a) => <Badge tone={levelTone[a.level]}>{levelText[a.level]}</Badge>,
    },
    {
      header: 'Tayang',
      cell: (a) => (
        <span className="text-xs">
          {a.startsAt ? dt(a.startsAt) : 'sekarang'} → {a.endsAt ? dt(a.endsAt) : 'tanpa batas'}
        </span>
      ),
    },
    {
      header: 'Status',
      cell: (a) => (a.active ? <Badge tone="success">Aktif</Badge> : <Badge>Nonaktif</Badge>),
    },
    {
      header: '',
      className: 'text-right',
      cell: (a) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              setForm({
                id: a.id,
                title: a.title,
                body: a.body,
                level: a.level,
                startsAt: toLocalInput(a.startsAt),
                endsAt: toLocalInput(a.endsAt),
                active: a.active,
              })
            }
          >
            Ubah
          </Button>
          <Button variant="ghost" onClick={() => void del.run(a)}>
            Hapus
          </Button>
        </div>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Pengumuman"
        subtitle="Tampil sebagai banner di Back Office seluruh tenant selama jendela waktunya"
        actions={
          <Button
            onClick={() =>
              setForm({
                title: '',
                body: '',
                level: 'info',
                startsAt: '',
                endsAt: '',
                active: true,
              })
            }
          >
            + Pengumuman
          </Button>
        }
      />
      {del.error && <Alert>{del.error}</Alert>}
      <Card>{q.isLoading ? 'Memuat…' : <DataTable columns={cols} rows={q.data ?? []} />}</Card>
      <Modal
        open={!!form}
        title={form?.id ? 'Ubah pengumuman' : 'Pengumuman baru'}
        onClose={() => setForm(null)}
      >
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            <TextField
              label="Judul"
              value={form.title}
              maxLength={100}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              autoFocus
            />
            <TextAreaField
              label="Isi"
              value={form.body}
              maxLength={600}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              required
            />
            <SelectField
              label="Tingkat"
              value={form.level}
              onChange={(e) => setForm({ ...form, level: e.target.value as Announcement['level'] })}
            >
              <option value="info">Info</option>
              <option value="warning">Peringatan</option>
              <option value="critical">Penting</option>
            </SelectField>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Mulai (opsional)"
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
              />
              <TextField
                label="Selesai (opsional)"
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />{' '}
              Aktif
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

// ======================= PAKET =======================
interface PlanRow {
  id: string;
  code: string;
  name: string;
  priceMonthly: number;
  limits: { outlets: number; devices: number; employees: number };
  features: Record<string, boolean>;
  isPublic: boolean;
  isPopular: boolean;
}

export function PlansPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['p-plans'],
    queryFn: () => platformApi.get<PlanRow[]>('/admin/plans'),
  });
  const [form, setForm] = useState<{
    id?: string;
    code: string;
    name: string;
    price: string;
    outlets: string;
    devices: string;
    employees: string;
    features: Record<string, boolean>;
    isPublic: boolean;
    isPopular: boolean;
  } | null>(null);
  const save = useSubmit(async () => {
    const body = {
      isPublic: form!.isPublic,
      isPopular: form!.isPopular,
      name: form!.name.trim(),
      priceMonthly: Number(form!.price || 0),
      limits: {
        outlets: Number(form!.outlets),
        devices: Number(form!.devices),
        employees: Number(form!.employees),
      },
      features: form!.features,
    };
    if (form!.id) await platformApi.patch(`/admin/plans/${form!.id}`, body);
    else await platformApi.post('/admin/plans', { code: form!.code.trim(), ...body });
    void qc.invalidateQueries({ queryKey: ['p-plans'] });
    setForm(null);
  });
  const cols: Column<PlanRow>[] = [
    { header: 'Kode', cell: (p) => <code>{p.code}</code> },
    {
      header: 'Nama',
      cell: (p) => (
        <span className="font-medium">
          {p.name}
          {p.isPopular && (
            <>
              {' '}
              <Badge tone="info">Populer</Badge>
            </>
          )}
        </span>
      ),
    },
    { header: 'Harga / bulan', cell: (p) => formatRupiah(p.priceMonthly) },
    { header: 'Di situs', cell: (p) => (p.isPublic ? 'Tampil' : '—') },
    { header: 'Outlet', cell: (p) => p.limits.outlets },
    { header: 'Perangkat', cell: (p) => p.limits.devices },
    { header: 'Pegawai', cell: (p) => p.limits.employees },
    {
      header: 'Fitur nonaktif',
      cell: (p) =>
        FEATURE_KEYS.filter((k) => !resolveFeatures(p.features)[k])
          .map((k) => FEATURES[k as FeatureKey])
          .join(', ') || '—',
    },
    {
      header: '',
      className: 'text-right',
      cell: (p) => (
        <Button
          variant="secondary"
          onClick={() =>
            setForm({
              id: p.id,
              code: p.code,
              name: p.name,
              price: String(p.priceMonthly),
              outlets: String(p.limits.outlets),
              devices: String(p.limits.devices),
              employees: String(p.limits.employees),
              features: { ...p.features },
              isPublic: p.isPublic,
              isPopular: p.isPopular,
            })
          }
        >
          Ubah
        </Button>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Paket Langganan"
        subtitle="Batas, harga, dan fitur paket"
        actions={
          <Button
            onClick={() =>
              setForm({
                code: '',
                name: '',
                price: '0',
                outlets: '1',
                devices: '2',
                employees: '5',
                features: {},
                isPublic: false,
                isPopular: false,
              })
            }
          >
            + Paket
          </Button>
        }
      />
      <Card>{isLoading ? 'Memuat…' : <DataTable columns={cols} rows={data ?? []} />}</Card>
      <Modal
        open={!!form}
        title={form?.id ? `Ubah paket ${form.code}` : 'Paket baru'}
        onClose={() => setForm(null)}
      >
        {form && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void save.run();
            }}
          >
            {save.error && <Alert>{save.error}</Alert>}
            {!form.id && (
              <TextField
                label="Kode (huruf kecil, angka, tanda minus)"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
              />
            )}
            <TextField
              label="Nama"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <TextField
              label="Harga per bulan (Rp)"
              inputMode="numeric"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value.replace(/\D/g, '') })}
            />
            <div className="grid grid-cols-3 gap-3">
              <TextField
                label="Maks. outlet"
                inputMode="numeric"
                value={form.outlets}
                onChange={(e) => setForm({ ...form, outlets: e.target.value.replace(/\D/g, '') })}
              />
              <TextField
                label="Maks. perangkat"
                inputMode="numeric"
                value={form.devices}
                onChange={(e) => setForm({ ...form, devices: e.target.value.replace(/\D/g, '') })}
              />
              <TextField
                label="Maks. pegawai"
                inputMode="numeric"
                value={form.employees}
                onChange={(e) => setForm({ ...form, employees: e.target.value.replace(/\D/g, '') })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isPublic}
                onChange={(e) => setForm({ ...form, isPublic: e.target.checked })}
              />
              Tampilkan di halaman harga publik
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isPopular}
                onChange={(e) => setForm({ ...form, isPopular: e.target.checked })}
              />
              Tandai &quot;Paling populer&quot; (hanya satu paket; tanda pada paket lain dilepas)
            </label>
            <fieldset>
              <legend className="mb-1 text-sm font-medium">Fitur paket</legend>
              <p className="mb-2 text-xs text-slate-500">
                Kasir inti, mode offline, katalog dasar, pajak &amp; struk, dan laporan dasar selalu
                aktif.
              </p>
              {FEATURE_GROUPS.map((g) => (
                <div key={g.title} className="mb-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {g.title}
                  </div>
                  {g.keys.map((k) => (
                    <label key={k} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={resolveFeatures(form.features)[k]}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            features: { ...resolveFeatures(form.features), [k]: e.target.checked },
                          })
                        }
                      />{' '}
                      {FEATURES[k]}
                    </label>
                  ))}
                </div>
              ))}
            </fieldset>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                Batal
              </Button>
              <Button type="submit" disabled={save.busy}>
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
